/**
 * Encabezado que se aparta al bajar y vuelve al subir.
 *
 * POR QUE ASI Y NO DE OTRA FORMA
 * ------------------------------
 * La tentacion es animar el ALTO del encabezado, o su `margin-top`. Las dos
 * cosas obligan al navegador a rehacer el reparto del marco en CADA cuadro, y
 * ademas mueven el contenido bajo el dedo: la lista da un tiron mientras el
 * encabezado se encoge. En un telefono barato eso se ve como un salto.
 *
 * Aqui el encabezado se saca del flujo y se mueve con `transform`, que el
 * navegador resuelve en el compositor —sin reparto y sin repintado—, y el
 * contenido NO se entera: no se mueve un pixel. Lo unico que cambia de sitio es
 * la barra. Es lo que hace que se sienta de aplicacion y no de pagina web.
 *
 * El hueco que el encabezado dejaria de ocupar se resuelve con un relleno
 * superior en la region que se desplaza, tomado de la altura MEDIDA de la barra
 * (no de un numero escrito a mano): asi sigue cuadrando con el area segura del
 * iPhone, con el telefono acostado y con la letra grande del sistema.
 *
 * CUANDO NO SE OCULTA, A PROPOSITO
 * --------------------------------
 *   - Con `prefers-reduced-motion`. Quien pidio menos movimiento no quiere que
 *     medio marco entre y salga mientras lee.
 *   - Si no hay suficiente que desplazar. Ocultar la barra en una pantalla que
 *     apenas se mueve deja al usuario sin sucursal ni estado de conexion a
 *     cambio de nada.
 *   - Cerca del borde superior. Volver arriba siempre devuelve la barra.
 *   - En la caja: ahi `main` no se desplaza (la pantalla llena el alto y la
 *     lista se mueve por dentro), asi que este gancho no llega a activarse.
 */
import { useEffect, useState, type RefObject } from 'react';
import { prefersReducedMotion } from './motion.js';

/**
 * Pixeles acumulados en una direccion antes de cambiar de estado.
 *
 * Sin este colchon la barra parpadea: el desplazamiento por inercia de iOS
 * reporta micro-rebotes de uno o dos pixeles en sentido contrario, y la barra
 * entraria y saldria sola con el dedo quieto.
 */
const UMBRAL = 12;

export function useHideOnScroll(
  scrollerRef: RefObject<HTMLElement | null>,
  headerRef: RefObject<HTMLElement | null>,
  /** Al cambiar de pantalla la barra vuelve, pase lo que pase. */
  resetKey: string,
): boolean {
  const [hidden, setHidden] = useState(false);

  // La barra vuelve en cada cambio de pantalla: la nueva empieza arriba.
  useEffect(() => {
    setHidden(false);
  }, [resetKey]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    const header = headerRef.current;
    if (!scroller || !header) return;
    if (prefersReducedMotion()) return;

    let ultimaY = Math.max(0, scroller.scrollTop);
    let acumulado = 0;
    let pendiente = false;

    const evaluar = (): void => {
      pendiente = false;
      const alto = header.offsetHeight;

      // En escritorio el encabezado no existe (`lg:hidden`), asi que mide 0 y
      // no hay nada que ocultar.
      if (alto === 0) {
        setHidden(false);
        return;
      }

      // `Math.max(0, ...)` descarta el rebote elastico de iOS, que reporta
      // posiciones negativas al estirar por arriba.
      const y = Math.max(0, scroller.scrollTop);
      const delta = y - ultimaY;
      ultimaY = y;

      // Sin recorrido suficiente, la barra se queda.
      if (scroller.scrollHeight - scroller.clientHeight < alto * 2) {
        setHidden(false);
        return;
      }

      // Zona de cortesia: mientras la barra todavia cabria en pantalla no se
      // esconde. Evita que un empujon minimo desde arriba se la lleve.
      if (y <= alto) {
        acumulado = 0;
        setHidden(false);
        return;
      }

      // El acumulado se reinicia al cambiar de sentido, para que el umbral se
      // mida desde el punto en que el dedo cambio de direccion y no desde el
      // principio del recorrido.
      acumulado = Math.sign(delta) === Math.sign(acumulado) ? acumulado + delta : delta;

      if (acumulado > UMBRAL) setHidden(true);
      else if (acumulado < -UMBRAL) setHidden(false);
    };

    // Un solo calculo por cuadro: el evento de desplazamiento se dispara mas
    // seguido que eso y no tiene caso medir dos veces lo mismo.
    const onScroll = (): void => {
      if (pendiente) return;
      pendiente = true;
      requestAnimationFrame(evaluar);
    };

    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [scrollerRef, headerRef, resetKey]);

  return hidden;
}

/**
 * Publica la altura MEDIDA del encabezado como variable CSS en el elemento que
 * se le indique, para que la region que se desplaza reserve ese espacio.
 *
 * Se mide en vez de escribirse porque el alto real depende del area segura del
 * aparato, de la orientacion y del tamaño de letra del sistema. Un `4rem` a
 * mano queda corto en un iPhone con muesca y sobra en un escritorio.
 */
export function usePublishedHeight(
  headerRef: RefObject<HTMLElement | null>,
  targetRef: RefObject<HTMLElement | null>,
  variable: string,
): void {
  useEffect(() => {
    const header = headerRef.current;
    const target = targetRef.current;
    if (!header || !target) return;

    const publicar = (): void => {
      target.style.setProperty(variable, `${header.offsetHeight}px`);
    };
    publicar();

    /*
     * `box: 'border-box'` NO sobra, y era un error de verdad.
     *
     * Por omision `ResizeObserver` vigila la CAJA DE CONTENIDO, y el area
     * segura del iPhone entra como `padding-top`. O sea que al aparecer la
     * muesca —al girar el aparato, o en el primer cuadro de un aparato con
     * muesca— el alto total de la barra pasaba de 57 a 116 px sin que la caja
     * de contenido cambiara un pixel: el observador no se enteraba y `main`
     * seguia reservando los 57 viejos, con la barra tapando el primer renglon.
     */
    const observer = new ResizeObserver(publicar);
    observer.observe(header, { box: 'border-box' });
    // El cambio de orientacion mueve el area segura sin cambiar el alto del
    // encabezado, asi que el observador solo no basta.
    window.addEventListener('orientationchange', publicar);
    window.addEventListener('resize', publicar);

    return () => {
      observer.disconnect();
      window.removeEventListener('orientationchange', publicar);
      window.removeEventListener('resize', publicar);
    };
  }, [headerRef, targetRef, variable]);
}
